"use client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSearchLogic } from "@/ui-sdk/hook/use-search-chat";
import { SendHorizonal, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useState } from "react";

const backendUrl = "https://backend-chat-b1b4.up.railway.app";

function FloatingChat() {
  const [searchValue, setSearchValue] = useState("");
  const {
    searchActive,
    setSearchActive,
    loading,
    handleInputChange,
    handleKeyDown,
    onSearch,
  } = useSearchLogic(searchValue, setSearchValue, backendUrl);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (searchActive) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 400);
    }
  }, [searchActive]);

  return (
    <div className="hp-fixed hp-bottom-10 hp-left-1/2 -hp-translate-x-1/2 hp-z-50 hp-flex hp-items-center hp-justify-center">
      <div className="hp-relative hp-flex hp-items-center hp-justify-center">
        {/* Search Icon (Hidden when expanded) */}
        <Button
          className={cn(
            "hp-mt-4 hp-flex hp-h-[45px] hp-cursor-pointer hp-items-center hp-p-2 hp-gap-2 hp-rounded-full hp-bg-white hp-px-2 hp-py-2 hp-pr-4 hp-text-lg hp-shadow md:hp-mt-0",
            {
              "!hp-px-0 !hp-gap-0 hp-py-0 hp-shadow-none": searchActive,
            },
          )}
          onClick={() => setSearchActive(true)}
          aria-label="Open search"
        >
          <div
            className={cn(
              "aurora hp-rounded-full hp-transition-all hp-duration-500 hp-ease-in-out",
              {
                "!hp-w-0 !hp-h-0": searchActive,
              },
            )}
          >
            <div className="wrapper">
              <div className="one"></div>
              <div className="two"></div>
              <div className="three"></div>
              <div className="four"></div>
            </div>
          </div>
          <div
            className={`hp-relative hp-max-w-[500px] hp-flex hp-items-center hp-transition-all hp-duration-500 hp-delay-50 hp-overflow-hidden hp-bg-white hp-rounded-full hp-shadow hp-p-[2px] ${
              searchActive
                ? "hp-w-[80vw] md:!hp-w-[400px] hp-opacity-100"
                : "hp-w-0 hp-opacity-0 hp-pointer-events-none"
            }`}
          >
            <div className="hp-animate-rotate hp-absolute hp-inset-0 !hp-block hp-h-full hp-w-full hp-rounded-full hp-bg-[conic-gradient(rgb(191,22,22,0.8)_20deg,transparent_120deg)]"></div>
            <div className="hp-flex hp-items-center hp-rounded-full hp-w-full hp-bg-white hp-z-20">
              <form
                className="hp-flex hp-w-full hp-items-center hp-space-x-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSearch();
                }}
              >
                <input
                  ref={inputRef}
                  className="hp-block hp-w-full hp-max-w-full hp-rounded-full hp-h-[40px] hp-bg-white hp-px-3 hp-py-1.5 hp-text-gray-900 placeholder:hp-text-gray-400 hp-text-2xl focus:hp-outline-none focus:hp-ring-0"
                  value={searchValue}
                  placeholder="Search for products"
                  onKeyDown={handleKeyDown}
                  onChange={handleInputChange}
                  disabled={loading}
                  aria-label="Search input"
                />
                <button
                  className="hp-h-12 hp-w-14 hp-rounded-full hp-bg-black hp-p-1"
                  type="submit"
                  disabled={loading || !searchValue.trim()}
                  aria-label="Submit search"
                >
                  <SendHorizonal className="hp-m-auto hp-h-8 hp-w-8 hp-cursor-pointer hp-text-white" />
                </button>
              </form>
            </div>
          </div>
          <button
            className={cn("hp-text-2xl hp-font-semibold hp-text-gray-500", {
              "!hp-w-0 !hp-h-0 hp-overflow-hidden hp-transition-all hp-duration-500 hp-ease-in-out":
                searchActive,
            })}
          >
            Search
          </button>
        </Button>

        {searchActive && (
          <button
            className="hp-absolute -hp-top-12 hp-left-1/2 hp-z-[999] hp-flex hp-h-10 hp-w-10 hp-p-1 hp-rounded-full hp-items-center hp-justify-center hp-bg-slate-200 -translate-x-1/2"
            onClick={() => setSearchActive(false)}
            aria-label="Close search"
          >
            <X className="hp-h-8 hp-w-8 hp-text-black" />
          </button>
        )}
      </div>
    </div>
  );
}

export default FloatingChat;
